import {
  PreviewOutlined as PreviewOutlinedIcon,
  RefreshOutlined as RefreshOutlinedIcon,
  SendOutlined as SendOutlinedIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Tab,
  Tabs,
} from "@mui/material";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import useNoti from "../../../../hooks/useNoti";
import { useTranslation } from "../../../../i18n/useTranslation";
import { AppNotifications } from "../../../../models";
import { HrReportService } from "../../../../services";
import Loading from "../../../components/Loading";
import NotificationModal from "../../../components/NotificationModal";
import SingleDatePicker from "../../../components/SinglePicker";
import { TEXT_HOLDER } from "../../../constants";
import { MESSAGES } from "../../../constants/mock";
import { sanitizeHtml } from "../../../../utils/sanitizeHtml";
import "./index.scss";

type AttendanceRecord = {
  employeeCode: string;
  employeeName: string;
  employeeEmail?: string;
  receiveEmails?: boolean;
  issues: string[];
  issueSummary: string;
  checkIn: string | null;
  checkOut: string | null;
  expectedIn: string;
  expectedOut: string;
  lateMinutes?: number;
};

type SummaryTotals = {
  onTime: number;
  late: number;
  absent: number;
  missingCheckout: number;
  needAction: number;
};

type SummaryData = {
  date: string;
  aiEnabled?: boolean;
  totals: SummaryTotals;
  actionItems: AttendanceRecord[];
  employees: AttendanceRecord[];
};

type PreviewEmail = {
  employeeCode?: string;
  email?: string;
  status: string;
  subject?: string;
  bodyHtml?: string;
  reason?: string;
  error?: string;
  actionItems?: Array<{
    employeeCode: string;
    employeeName: string;
    issueSummary: string;
    checkIn: string | null;
    checkOut: string | null;
  }>;
};

type EmailLog = {
  _id: string;
  date: string;
  type: string;
  recipientEmail: string;
  recipientName: string;
  employeeCode?: string;
  subject: string;
  status: string;
  createdAt: string;
};

type P_Props = {
  setCount: (num: number) => void;
};

type IssueFilter = "absent" | "late" | "missing_checkout";

const ISSUE_FILTERS: Array<{
  value: IssueFilter;
  labelKey: string;
  color: "error" | "warning" | "info";
}> = [
  { value: "absent", labelKey: "aiReport.absent", color: "error" },
  { value: "late", labelKey: "aiReport.late", color: "warning" },
  { value: "missing_checkout", labelKey: "aiReport.missingCheckout", color: "info" },
];

const issueColor = (issues: string[]) => {
  if (issues.includes("absent")) return "error";
  if (issues.includes("late")) return "warning";
  if (issues.includes("missing_checkout")) return "info";
  return "default";
};

const checkboxSx = {
  padding: 0,
  "& .MuiSvgIcon-root": {
    fontSize: 20,
    color: "#10BED2",
  },
};

const AiDailyReport: React.FC<P_Props> = ({ setCount }) => {
  const { t } = useTranslation();
  const [pushNoti] = useNoti();
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTab, setPreviewTab] = useState(0);
  const [previewData, setPreviewData] = useState<{
    employeeEmails: PreviewEmail[];
    hrEmails: PreviewEmail[];
  } | null>(null);
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);
  const [issueFilters, setIssueFilters] = useState<IssueFilter[]>([]);

  const matchesIssueFilter = useCallback(
    (item: AttendanceRecord) => {
      if (!issueFilters.length) {
        return true;
      }
      return issueFilters.some((filter) => item.issues.includes(filter));
    },
    [issueFilters]
  );

  const filteredActionItems =
    summary?.actionItems?.filter(matchesIssueFilter) || [];

  const selectableItems =
    filteredActionItems.filter(
      (item) => item.employeeEmail && item.receiveEmails !== false
    );
  const allSelectableSelected =
    selectableItems.length > 0 &&
    selectableItems.every((item) => selectedCodes.includes(item.employeeCode));
  const someSelectableSelected =
    selectableItems.some((item) => selectedCodes.includes(item.employeeCode)) &&
    !allSelectableSelected;

  const getReportRequest = useCallback(
    () => ({
      date,
      employeeCodes: selectedCodes,
    }),
    [date, selectedCodes]
  );

  const toggleEmployee = (employeeCode: string) => {
    setSelectedCodes((prev) =>
      prev.includes(employeeCode)
        ? prev.filter((code) => code !== employeeCode)
        : [...prev, employeeCode]
    );
  };

  const toggleSelectAll = () => {
    if (allSelectableSelected) {
      setSelectedCodes((prev) =>
        prev.filter(
          (code) => !selectableItems.some((item) => item.employeeCode === code)
        )
      );
      return;
    }
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      selectableItems.forEach((item) => next.add(item.employeeCode));
      return Array.from(next);
    });
  };

  const toggleIssueFilter = (filter: IssueFilter) => {
    setIssueFilters((prev) =>
      prev.includes(filter)
        ? prev.filter((value) => value !== filter)
        : [...prev, filter]
    );
  };

  const getDefaultSelectedCodes = (data: SummaryData) =>
    data.actionItems
      ?.filter((item) => item.employeeEmail)
      .map((item) => item.employeeCode) || [];

  const loadSummary = useCallback(
    async (resetSelection = false) => {
      setLoading(true);
      try {
        const res = await HrReportService.getSummary(date);
        if (res?.status === 200 && res.data?.data) {
          const data = res.data.data as SummaryData;
          setSummary(data);
          setCount(data.totals?.needAction || 0);
          if (resetSelection) {
            setSelectedCodes(getDefaultSelectedCodes(data));
          }
        } else {
          pushNoti(AppNotifications.ERROR, res?.data?.message || MESSAGES.ERROR);
        }
      } catch (error) {
        pushNoti(AppNotifications.ERROR, MESSAGES.ERROR);
      } finally {
        setLoading(false);
      }
    },
    [date, pushNoti, setCount]
  );

  const fetchLogs = useCallback(async () => {
    try {
      const res = await HrReportService.getLogs({ date, page: 1, limit: 100 });
      if (res?.status === 200 && res.data?.data?.items) {
        setLogs(res.data.data.items);
      }
    } catch (error) {
      console.log(error);
    }
  }, [date]);

  useEffect(() => {
    setIssueFilters([]);
    loadSummary(true);
    fetchLogs();
    // Only reload when report date changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const handlePreview = async () => {
    setActionLoading(true);
    try {
      const res = await HrReportService.preview(getReportRequest());
      if (res?.status === 200 && res.data?.data) {
        setPreviewData({
          employeeEmails: res.data.data.employeeEmails || [],
          hrEmails: Array.isArray(res.data.data.hrEmails)
            ? res.data.data.hrEmails
            : res.data.data.hrEmails
            ? [res.data.data.hrEmails]
            : [],
        });
        setPreviewTab(0);
        setPreviewOpen(true);
        pushNoti(AppNotifications.SUCCESS, "Preview generated");
      } else {
        pushNoti(AppNotifications.ERROR, res?.data?.message || MESSAGES.ERROR);
      }
    } catch (error) {
      pushNoti(AppNotifications.ERROR, MESSAGES.ERROR);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSend = async () => {
    setActionLoading(true);
    try {
      const res = await HrReportService.send(getReportRequest());
      if (res?.status === 200) {
        pushNoti(AppNotifications.SUCCESS, MESSAGES.SUCCESS);
        setConfirmSend(false);
        fetchLogs();
      } else {
        pushNoti(AppNotifications.ERROR, res?.data?.message || MESSAGES.ERROR);
      }
    } catch (error) {
      pushNoti(AppNotifications.ERROR, MESSAGES.ERROR);
    } finally {
      setActionLoading(false);
    }
  };

  const previewItems =
    previewTab === 0
      ? previewData?.employeeEmails || []
      : previewData?.hrEmails || [];

  if (loading && !summary) {
    return <Loading />;
  }

  return (
    <div className='ai-daily-report'>
      <div className='ai-daily-report__toolbar'>
        <div className='ai-daily-report__toolbar__left'>
          <span className='ai-daily-report__label'>Report date</span>
          <SingleDatePicker
            day={moment(date)}
            onChangeDate={(value) => setDate(value)}
            iconStart
          />
          {summary?.aiEnabled === false && (
            <Chip
              size='small'
              label='AI fallback mode'
              color='warning'
              variant='outlined'
            />
          )}
        </div>
        <div className='ai-daily-report__toolbar__right'>
          <Button
            variant='outlined'
            startIcon={<RefreshOutlinedIcon />}
            onClick={() => {
              loadSummary(false);
              fetchLogs();
            }}
            disabled={loading || actionLoading}
          >
            Refresh
          </Button>
          <Button
            variant='outlined'
            startIcon={<PreviewOutlinedIcon />}
            onClick={handlePreview}
            disabled={actionLoading}
          >
            Preview
          </Button>
          <Button
            variant='contained'
            startIcon={<SendOutlinedIcon />}
            onClick={() => setConfirmSend(true)}
            disabled={actionLoading}
          >
            Send emails
          </Button>
        </div>
      </div>

      <div className='ai-daily-report__cards'>
        <div className='ai-daily-report__card ai-daily-report__card--success'>
          <strong>{summary?.totals?.onTime ?? 0}</strong>
          <span>{t("aiReport.onTime")}</span>
        </div>
        <div className='ai-daily-report__card ai-daily-report__card--warning'>
          <strong>{summary?.totals?.late ?? 0}</strong>
          <span>{t("aiReport.late")}</span>
        </div>
        <div className='ai-daily-report__card ai-daily-report__card--error'>
          <strong>{summary?.totals?.absent ?? 0}</strong>
          <span>{t("aiReport.absent")}</span>
        </div>
        <div className='ai-daily-report__card ai-daily-report__card--info'>
          <strong>{summary?.totals?.missingCheckout ?? 0}</strong>
          <span>{t("aiReport.missingCheckout")}</span>
        </div>
      </div>

      <div className='ai-daily-report__section ai-daily-report__section--actions'>
        <div className='ai-daily-report__section__title'>
          <span>
            Cases needing action (
            {issueFilters.length
              ? `${filteredActionItems.length}/${summary?.actionItems?.length || 0}`
              : summary?.actionItems?.length || 0}
            )
          </span>
          {summary?.actionItems?.length ? (
            <span className='ai-daily-report__section__meta'>
              {selectedCodes.length} selected to receive email
            </span>
          ) : null}
        </div>
        {summary?.actionItems?.length ? (
          <div className='ai-daily-report__filters'>
            <span className='ai-daily-report__filters__label'>Filter by status:</span>
            {ISSUE_FILTERS.map((filter) => (
              <Chip
                key={filter.value}
                label={t(filter.labelKey)}
                color={filter.color}
                variant={issueFilters.includes(filter.value) ? "filled" : "outlined"}
                clickable
                onClick={() => toggleIssueFilter(filter.value)}
              />
            ))}
            {issueFilters.length ? (
              <button
                type='button'
                className='ai-daily-report__filters__clear'
                onClick={() => setIssueFilters([])}
              >
                Clear filters
              </button>
            ) : null}
          </div>
        ) : null}
        <div className='ai-daily-report__table ai-daily-report__table--actions'>
          <div className='ai-daily-report__table__header'>
            <div className='col-select'>
              <Checkbox
                sx={checkboxSx}
                checked={allSelectableSelected}
                indeterminate={someSelectableSelected}
                disabled={!selectableItems.length}
                onChange={toggleSelectAll}
              />
            </div>
            <div className='col-code'>Code</div>
            <div className='col-name'>Employee</div>
            <div className='col-email'>Email</div>
            <div className='col-checkin'>Check-in</div>
            <div className='col-checkout'>Check-out</div>
            <div className='col-expected'>Shift start</div>
            <div className='col-issues'>Issues</div>
          </div>
          <div className='ai-daily-report__table__body'>
            {summary?.actionItems?.length ? (
              filteredActionItems.length ? (
                filteredActionItems.map((item) => {
                const canSelect =
                  Boolean(item.employeeEmail) && item.receiveEmails !== false;
                const isSelected = selectedCodes.includes(item.employeeCode);
                const optOutTitle = !item.employeeEmail
                  ? "Employee has no email"
                  : item.receiveEmails === false
                  ? "Employee opted out of emails"
                  : undefined;

                return (
                  <div
                    className={`ai-daily-report__table__row ${
                      isSelected ? "ai-daily-report__table__row--selected" : ""
                    }`}
                    key={item.employeeCode}
                  >
                    <div className='col-select'>
                      <Checkbox
                        sx={checkboxSx}
                        checked={isSelected}
                        disabled={!canSelect}
                        onChange={() => toggleEmployee(item.employeeCode)}
                        title={optOutTitle}
                      />
                    </div>
                    <div className='col-code'>{item.employeeCode}</div>
                    <div className='col-name'>{item.employeeName}</div>
                    <div className='col-email'>
                      {item.employeeEmail || TEXT_HOLDER}
                      {item.receiveEmails === false ? (
                        <Chip
                          size='small'
                          label='Opted out'
                          color='default'
                          variant='outlined'
                          sx={{ ml: 1 }}
                        />
                      ) : null}
                    </div>
                    <div className='col-checkin'>{item.checkIn || TEXT_HOLDER}</div>
                    <div className='col-checkout'>{item.checkOut || TEXT_HOLDER}</div>
                    <div className='col-expected'>{item.expectedIn || TEXT_HOLDER}</div>
                    <div className='col-issues'>
                      <Chip
                        size='small'
                        label={item.issueSummary}
                        color={issueColor(item.issues) as any}
                        variant='outlined'
                      />
                    </div>
                  </div>
                );
              })
              ) : (
                <div className='ai-daily-report__empty'>
                  No cases match the selected filters.
                </div>
              )
            ) : (
              <div className='ai-daily-report__empty'>
                No attendance issues for this date.
              </div>
            )}
          </div>
        </div>
      </div>

      <div className='ai-daily-report__section ai-daily-report__section--logs'>
        <div className='ai-daily-report__section__title'>Send history</div>
        <div className='ai-daily-report__table ai-daily-report__table--logs'>
          <div className='ai-daily-report__table__header'>
            <div className='col-time'>Time</div>
            <div className='col-type'>Type</div>
            <div className='col-recipient'>Recipient</div>
            <div className='col-subject'>Subject</div>
            <div className='col-status'>Status</div>
          </div>
          <div className='ai-daily-report__table__body'>
            {logs.length ? (
              logs.map((log) => (
                <div className='ai-daily-report__table__row' key={log._id}>
                  <div className='col-time'>
                    {moment(log.createdAt).format("DD/MM/YYYY HH:mm")}
                  </div>
                  <div className='col-type'>
                    {log.type === "hr_summary" ? "HR summary" : "Employee"}
                  </div>
                  <div className='col-recipient'>{log.recipientEmail}</div>
                  <div className='col-subject'>{log.subject}</div>
                  <div className='col-status'>
                    <Chip
                      size='small'
                      label={log.status}
                      color={
                        log.status === "sent"
                          ? "success"
                          : log.status === "failed"
                          ? "error"
                          : "default"
                      }
                      variant='outlined'
                    />
                  </div>
                </div>
              ))
            ) : (
              <div className='ai-daily-report__empty'>No email logs yet.</div>
            )}
          </div>
        </div>
      </div>

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>Email preview</DialogTitle>
        <DialogContent dividers>
          <Tabs
            value={previewTab}
            onChange={(_, value) => setPreviewTab(value)}
            sx={{ mb: 2 }}
          >
            <Tab label={`Employees (${previewData?.employeeEmails?.length || 0})`} />
            <Tab label={`HR (${previewData?.hrEmails?.length || 0})`} />
          </Tabs>
          {previewItems.length ? (
            previewItems.map((item, index) => (
              <Box key={`${item.employeeCode || item.email}-${index}`} sx={{ mb: 3 }}>
                <div className='ai-daily-report__preview-meta'>
                  <strong>{item.subject || "No subject"}</strong>
                  <Chip size='small' label={item.status} sx={{ ml: 1 }} />
                </div>
                {previewTab === 1 && item.actionItems?.length ? (
                  <div className='ai-daily-report__preview-detail'>
                    <strong>Danh sách chi tiết</strong>
                    <table>
                      <thead>
                        <tr>
                          <th>Mã NV</th>
                          <th>Họ tên</th>
                          <th>Tình trạng</th>
                          <th>Check-in</th>
                          <th>Check-out</th>
                        </tr>
                      </thead>
                      <tbody>
                        {item.actionItems.map((row) => (
                          <tr key={row.employeeCode}>
                            <td>{row.employeeCode}</td>
                            <td>{row.employeeName}</td>
                            <td>{row.issueSummary}</td>
                            <td>{row.checkIn || TEXT_HOLDER}</td>
                            <td>{row.checkOut || TEXT_HOLDER}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
                {previewTab !== 1 && item.bodyHtml ? (
                  <div
                    className='ai-daily-report__preview-body'
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.bodyHtml) }}
                  />
                ) : previewTab === 1 && !item.actionItems?.length ? (
                  <div className='ai-daily-report__empty'>
                    {item.reason || item.error || "No preview content"}
                  </div>
                ) : null}
                {index < previewItems.length - 1 && <Divider sx={{ mt: 2 }} />}
              </Box>
            ))
          ) : (
            <div className='ai-daily-report__empty'>No preview emails.</div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <NotificationModal
        visible={confirmSend}
        title='Send daily report emails'
        icon={
          <div className='notification-icon-box green'>
            <SendOutlinedIcon style={{ fontSize: "7rem", color: "#fff" }} />
          </div>
        }
        subTitle={
          <span>
            {selectedCodes.length ? (
              <>
                Send reminder emails to{" "}
                <strong>{selectedCodes.length}</strong> selected employee(s) and
                HR summary for <strong>{date}</strong>?
              </>
            ) : (
              <>
                Send HR summary only for <strong>{date}</strong> (no employee
                selected)?
              </>
            )}
          </span>
        }
        actions={
          <>
            <button
              className='btn-cancel-style'
              onClick={() => setConfirmSend(false)}
            >
              Cancel
            </button>
            <button
              className='btn-confirm-style'
              onClick={handleSend}
              disabled={actionLoading}
            >
              Send
            </button>
          </>
        }
        onClose={() => setConfirmSend(false)}
      />
    </div>
  );
};

export default AiDailyReport;
